import * as Sentry from "@sentry/browser";
import { useMutation } from "react-query";
import * as Redux from "react-redux";
import { Dispatch } from "redux";

import { logAmplitudeEvent } from "client/lib/amplitude";
import { CountryList, Merchant } from "client/lib/apt-merchant";
import { addToGTMDataLayer } from "client/lib/gtm";
import { FormService } from "client/services";
import { errorActions, formCompletionActions } from "client/store";
import { UserStateResponse } from "client/types";

import { useMerchantModel } from "./useMerchantModel";

type UseSubmitBankInfo = {
  onError?: (args: Error) => void;
  onSuccess?: (args: UserStateResponse) => void;
};

const getDefaultOnSuccessCb = (dispatch: Dispatch, merchant: Merchant) => ({
  validation_errors,
  formCompletedState,
}: UserStateResponse) => {
  if (validation_errors) {
    addToGTMDataLayer({
      event: "PayInfoUpload",
      formSubmission: "Unsuccessful",
      country: merchant.companyInformation.registeredCountry.value,
    });
    dispatch(errorActions.setFieldErrorMap(validation_errors));
  } else if (formCompletedState) {
    dispatch(formCompletionActions.setFormCompletion(formCompletedState));
    logAmplitudeEvent("REDESIGN_LINK_YOUR_BANK_ACCOUNT", "submitted_bank_info");
    addToGTMDataLayer({
      event: "PayInfoUpload",
      formSubmission: "Successful",
      country: merchant.companyInformation.registeredCountry.value,
    });
  } else {
    dispatch(errorActions.setError("Unexpected error"));
    Sentry.captureException(
      new Error(
        `Unexpected error submitting bank info, merchantId: ${
          merchant.merchantAccount?.merchantId ?? "N/A"
        }, mlsProspectRef: ${merchant.merchantAccount?.mlsProspectRef ?? "N/A"}`
      )
    );
  }
};

const getDefaultOnErrorCb = (dispatch: Dispatch, country?: CountryList) => (
  err: Error
) => {
  addToGTMDataLayer({
    event: "PayInfoUpload",
    formSubmission: "Unsuccessful",
    country,
  });
  dispatch(errorActions.setError(err.message));
};

export function useSubmitBankInfo({
  onError,
  onSuccess,
}: UseSubmitBankInfo): {
  submitBankInfo: (args: any) => void;
} {
  const { merchant } = useMerchantModel();
  const country = merchant.companyInformation.registeredCountry.value;
  const dispatch = Redux.useDispatch();
  const { mutate } = useMutation(FormService.completeBankInfoForm, {
    onError: onError || getDefaultOnErrorCb(dispatch, country),
    onSuccess: onSuccess || getDefaultOnSuccessCb(dispatch, merchant),
  });

  return { submitBankInfo: mutate };
}
